/* "Action with reason" — the one confirmation dialog the whole console uses.
 *
 * The spec names reason capture as a pattern rather than a one-off, and this is
 * why: a status changed by an operator with no reason on it is an unwinnable
 * dispute three weeks later, and the audit log is only worth keeping if the
 * reason column is populated. So the confirm button is DISABLED until a reason
 * code is chosen, and the note is asked for in the operator's own words rather
 * than a dropdown of near-misses.
 *
 * Every caller also states the impact in plain language before the click — what
 * changes, who is told, whether money moves — because "cancel booking" is not a
 * description of a consequence.
 */

import { useState, type ReactNode } from 'react';
import { Modal } from './Drawer';
import css from './ui.module.css';
import { Btn, Field, Textarea } from './primitives';

export interface ReasonCode {
  code: string;
  label: string;
}

export function ActionWithReason({
  open,
  onClose,
  title,
  desc,
  optLabel,
  options,
  codes,
  impact,
  okLabel = 'Confirm',
  okTone = 'primary',
  fields,
  blocked,
  noteRequired,
  kicker = 'Confirm',
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  desc: string;
  /** An optional single-choice list above the reason codes — the booking
   *  drawer's "reassign to" list, the rate card's "effective from". */
  optLabel?: string;
  options?: readonly string[];
  codes: readonly ReasonCode[];
  impact?: string;
  okLabel?: string;
  okTone?: 'primary' | 'ok' | 'danger';
  /** The action's own inputs — a reschedule's new date, a refund's amount —
   *  rendered above the reason codes. The caller owns their state. */
  fields?: ReactNode;
  /** Why the confirm is held even with a reason chosen (an empty amount, a date
   *  not yet picked). null or undefined: nothing is blocking. */
  blocked?: string | null;
  /** Why the server will refuse this without a note in words (keeping a
   *  deposit, an unjustified finding). Set: confirm waits for a note. */
  noteRequired?: string | null;
  /** The small line above the title. The console's says "audited"; the agency
   *  app's actions are recorded by the server but not in the console's audit
   *  log, so it says what the action is instead. */
  kicker?: string;
  onConfirm: (result: { code: string; note: string; option: string | null }) => void;
}) {
  const [code, setCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [option, setOption] = useState<string | null>(null);
  // An empty code list means the server takes no reason for this action (a
  // payout settlement, a dialog that only informs). Requiring a code from an
  // empty list left the confirm button disabled for ever: "Settle it" could
  // never be pressed (found 2026-10-16).
  const needsCode = codes.length > 0;

  function reset() {
    setCode(null);
    setNote('');
    setOption(null);
  }

  return (
    <Modal
      open={open}
      label={title}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <div className={css.modalHead}>
        <div className={css.label}>{kicker}</div>
        <div className={css.modalTitle}>{title}</div>
        <div className={css.modalDesc}>{desc}</div>
      </div>

      <div className={css.modalBody}>
        {fields}
        {options && options.length > 0 ? (
          <div>
            <div className={css.label} style={{ marginBottom: 7 }}>
              {optLabel ?? 'Option'}
            </div>
            <div className={css.chipsLg}>
              {options.map((o) => (
                <button
                  key={o}
                  type="button"
                  className={`${css.chip} ${option === o ? css.chipOn : ''}`}
                  style={{ minHeight: 32, padding: '0 11px', font: '700 12px var(--font-ui)' }}
                  onClick={() => setOption(o)}
                  aria-pressed={option === o}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {needsCode ? (
        <div>
          <div className={css.label} style={{ marginBottom: 7 }}>
            Reason code
          </div>
          <div className={css.chipsLg}>
            {codes.map((c) => (
              <button
                key={c.code}
                type="button"
                title={c.label}
                className={`${css.btn} ${css.btnSm}`}
                style={
                  code === c.code
                    ? { background: 'var(--navy)', color: 'var(--ink-invert)', border: 0, font: '700 11.5px var(--font-mono)' }
                    : { font: '700 11.5px var(--font-mono)' }
                }
                onClick={() => setCode(c.code)}
                aria-pressed={code === c.code}
              >
                {c.code}
              </button>
            ))}
          </div>
          {code ? (
            <div className={css.sub} style={{ marginTop: 6 }}>
              {codes.find((c) => c.code === code)?.label}
            </div>
          ) : null}
        </div>
        ) : null}

        {needsCode ? (
          <Field label="Note">
            <Textarea value={note} onChange={setNote} placeholder="What happened and why" rows={3} />
          </Field>
        ) : null}

        {impact ? <div className={css.noteAmber + ' ' + css.note}>{impact}</div> : null}
      </div>

      <div className={css.modalFoot}>
        <Btn
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Back
        </Btn>
        <Btn
          variant={okTone}
          disabled={(needsCode && !code) || !!blocked || (!!noteRequired && !note.trim())}
          title={
            needsCode && !code
              ? 'Choose a reason first'
              : (blocked ?? (noteRequired && !note.trim() ? noteRequired : undefined))
          }
          onClick={() => {
            if ((needsCode && !code) || blocked || (noteRequired && !note.trim())) return;
            onConfirm({ code: code ?? '', note, option });
            reset();
          }}
        >
          {okLabel}
        </Btn>
      </div>
    </Modal>
  );
}
